import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const blankItem = () => ({
  product_id: '',
  product_name: '',
  product_code: '',
  batch_id: '',
  batch_no: '',
  batches: [],           // batches available for this product (loaded on product change)
  loadingBatches: false,
  quantity: '',
  unit: 'Kg',
  rate: '',
  gst_percent: '',       // blank by default (owner input only when needed)
  packaging_qty: '',
  remarks: '',
});

const OutwardFormModal = ({ onClose, onSaved }) => {
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loadingMaster, setLoadingMaster] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    gp_date: new Date().toISOString().split('T')[0],
    customer_id: '',
    rider_name: '',
    rider_cnic: '',
    rider_phone: '',
    vehicle_no: '',
    status: 'issued',
    amount_paid: '',
    payment_mode: 'cash',
    remarks: '',
    items: [blankItem()],
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingMaster(true);
        const [custRes, prodRes] = await Promise.all([
          chemicalService.getCustomers({ page: 1, limit: 1000 }),
          chemicalService.getProducts({ page: 1, limit: 1000 }),
        ]);
        setCustomers((custRes.data || []).filter((c) => c.is_active));
        setProducts((prodRes.data || []).filter((p) => p.is_active));
      } catch (err) {
        console.error('Error loading data:', err);
        alert('❌ Failed to load data');
      } finally {
        setLoadingMaster(false);
      }
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // Load batches when product changes
  const handleProductChange = async (index, productId) => {
    setForm((prev) => {
      const items = [...prev.items];
      const product = products.find((p) => p.id === parseInt(productId, 10));
      items[index] = {
        ...items[index],
        product_id: productId,
        product_name: product?.name || '',
        product_code: product?.code || '',
        unit: product?.base_unit || 'Kg',
        batch_id: '',
        batch_no: '',
        batches: [],
        loadingBatches: !!productId,
      };
      return { ...prev, items };
    });

    if (!productId) return;

    try {
      const res = await chemicalService.getBatchesByProduct(productId);
      const batches = (res.data || []).filter((b) => parseFloat(b.qty_available) > 0);
      setForm((prev) => {
        const items = [...prev.items];
        if (items[index] && items[index].product_id === productId) {
          items[index] = { ...items[index], batches, loadingBatches: false };
        }
        return { ...prev, items };
      });
    } catch (err) {
      console.error('Error loading batches:', err);
      setForm((prev) => {
        const items = [...prev.items];
        if (items[index]) items[index] = { ...items[index], loadingBatches: false };
        return { ...prev, items };
      });
    }
  };

  const handleItemChange = (index, field, value) => {
    setForm((prev) => {
      const items = [...prev.items];
      items[index] = { ...items[index], [field]: value };
      // Auto-fill batch_no when batch_id is selected
      if (field === 'batch_id') {
        const batch = items[index].batches.find((b) => String(b.id) === String(value));
        items[index].batch_no = batch?.batch_no || '';
      }
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm((prev) => ({ ...prev, items: [...prev.items, blankItem()] }));
  };

  const removeItem = (index) => {
    if (form.items.length === 1) return alert('❌ At least one item required.');
    setForm((prev) => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  // ─── Live totals ────────────────────────────────
  const lineAmount = (it) => (parseFloat(it.quantity) || 0) * (parseFloat(it.rate) || 0);
  const lineGst = (it) => lineAmount(it) * ((parseFloat(it.gst_percent) || 0) / 100);
  const subtotal = form.items.reduce((s, it) => s + lineAmount(it), 0);
  const gstAmount = form.items.reduce((s, it) => s + lineGst(it), 0);
  const grandTotal = subtotal + gstAmount;
  const amountPaid = parseFloat(form.amount_paid) || 0;
  const balance = grandTotal - amountPaid;

  const fmtMoney = (n) => 'Rs ' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-PK');
  const fmtQty = (n) => parseFloat(n || 0).toFixed(2).replace(/\.?0+$/, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.customer_id) return alert('❌ Select a customer.');
    if (!form.gp_date) return alert('❌ Date is required.');
    if (!form.rider_name.trim()) return alert('❌ Rider name is required.');
    if (!form.vehicle_no.trim()) return alert('❌ Vehicle number is required.');

    for (let i = 0; i < form.items.length; i++) {
      const it = form.items[i];
      const idx = i + 1;
      if (!it.product_id) return alert(`❌ Item ${idx}: select a product.`);
      if (!it.batch_id) return alert(`❌ Item ${idx}: select a batch.`);
      const qty = parseFloat(it.quantity) || 0;
      if (qty <= 0) return alert(`❌ Item ${idx}: quantity must be > 0.`);
      if (!it.rate || parseFloat(it.rate) < 0) return alert(`❌ Item ${idx}: rate must be > 0.`);
      const batch = it.batches.find((b) => String(b.id) === String(it.batch_id));
      const available = parseFloat(batch?.qty_available) || 0;
      if (qty > available) return alert(`❌ Item ${idx}: insufficient stock in ${it.batch_no} (available ${fmtQty(available)}, needed ${fmtQty(qty)}).`);
    }

    if (amountPaid > grandTotal) return alert('❌ Amount paid cannot exceed grand total.');

    try {
      setSubmitting(true);
      const payload = {
        gp_date: form.gp_date,
        customer_id: parseInt(form.customer_id, 10),
        rider_name: form.rider_name.trim(),
        rider_cnic: form.rider_cnic.trim() || null,
        rider_phone: form.rider_phone.trim() || null,
        vehicle_no: form.vehicle_no.trim(),
        status: form.status,
        remarks: form.remarks.trim() || null,
        subtotal: Math.round(subtotal * 100) / 100,
        gst_amount: Math.round(gstAmount * 100) / 100,
        gst_percent: 0, // Per-line GST percents drive the total; header stores 0
        freight: 0,
        discount: 0,
        grand_total: Math.round(grandTotal * 100) / 100,
        amount_paid: amountPaid,
        payment_mode: form.payment_mode,
        items: form.items.map((it) => ({
          product_id: parseInt(it.product_id, 10),
          batch_id: parseInt(it.batch_id, 10),
          batch_no: it.batch_no,
          quantity: parseFloat(it.quantity),
          unit: it.unit,
          rate: parseFloat(it.rate),
          discount_percent: 0,
          amount: Math.round(lineAmount(it) * 100) / 100,
          packaging_qty: it.packaging_qty === '' ? null : parseFloat(it.packaging_qty),
          remarks: it.remarks.trim() || null,
        })),
      };

      const res = await chemicalService.createOutwardGatePass(payload);
      const gpNo = res.data?.gp_no || '';
      const invNo = res.data?.invoice_no || '';
      alert(`✅ Outward gate pass saved!${gpNo ? `\nGate Pass: ${gpNo}` : ''}${invNo ? `\nInvoice: ${invNo}` : ''}`);
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save outward gate pass';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>↑ New Outward (Sale + Gate Pass)</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Creates invoice, deducts stock, and issues gate pass together
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            {/* Header */}
            <div style={styles.sectionLabel}>Customer & Header</div>
            <div style={styles.row}>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>Customer <span style={styles.required}>*</span></label>
                <select
                  value={form.customer_id}
                  onChange={(e) => handleChange('customer_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingMaster}
                >
                  <option value="">
                    {loadingMaster ? 'Loading customers...' : customers.length === 0 ? '-- No active customers --' : '-- Select customer --'}
                  </option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Date <span style={styles.required}>*</span></label>
                <input type="date" value={form.gp_date} onChange={(e) => handleChange('gp_date', e.target.value)} style={styles.input} required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Status</label>
                <select value={form.status} onChange={(e) => handleChange('status', e.target.value)} style={styles.input}>
                  <option value="issued">▶ Issued</option>
                  <option value="delivered">✓ Delivered</option>
                </select>
              </div>
            </div>

            {/* Rider */}
            <div style={styles.sectionLabel}>Rider & Vehicle</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider Name <span style={styles.required}>*</span></label>
                <input type="text" value={form.rider_name} onChange={(e) => handleChange('rider_name', e.target.value)} style={styles.input} placeholder="e.g. Imran Ali" required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Vehicle No <span style={styles.required}>*</span></label>
                <input type="text" value={form.vehicle_no} onChange={(e) => handleChange('vehicle_no', e.target.value)} style={styles.input} placeholder="e.g. FDD-3247" required />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider CNIC</label>
                <input type="text" value={form.rider_cnic} onChange={(e) => handleChange('rider_cnic', e.target.value)} style={styles.input} placeholder="optional" />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Rider Phone</label>
                <input type="tel" value={form.rider_phone} onChange={(e) => handleChange('rider_phone', e.target.value)} style={styles.input} placeholder="optional" />
              </div>
            </div>

            {/* Items */}
            <div style={styles.sectionLabel}>Items ({form.items.length})</div>
            <div style={styles.itemsList}>
              {form.items.map((item, index) => (
                <div key={index} style={styles.itemCard}>
                  <div style={styles.itemHeader}>
                    <div style={styles.itemIndex}>{index + 1}</div>
                    <span style={styles.itemTitle}>Item {index + 1}</span>
                    <span style={styles.lineAmount}>{fmtMoney(lineAmount(item) + lineGst(item))}</span>
                    <button type="button" onClick={() => removeItem(index)} style={styles.removeButton} disabled={form.items.length === 1}>🗑️</button>
                  </div>

                  <div style={styles.itemRow}>
                    <div style={{ flex: 3, minWidth: '200px' }}>
                      <label style={styles.miniLabel}>Product *</label>
                      <select
                        value={item.product_id}
                        onChange={(e) => handleProductChange(index, e.target.value)}
                        style={styles.input}
                        required
                        disabled={loadingMaster}
                      >
                        <option value="">-- Select --</option>
                        {products.map((p) => (
                          <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 2, minWidth: '160px' }}>
                      <label style={styles.miniLabel}>Batch *</label>
                      <select
                        value={item.batch_id}
                        onChange={(e) => handleItemChange(index, 'batch_id', e.target.value)}
                        style={styles.input}
                        required
                        disabled={!item.product_id || item.loadingBatches}
                      >
                        <option value="">
                          {item.loadingBatches ? 'Loading...'
                            : !item.product_id ? '-- Pick product first --'
                            : item.batches.length === 0 ? '-- No stock available --'
                            : '-- Select batch --'}
                        </option>
                        {item.batches.map((b) => (
                          <option key={b.id} value={b.id}>
                            {b.batch_no} · Avail: {fmtQty(b.qty_available)} {item.unit}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ flex: 1, minWidth: '100px' }}>
                      <label style={styles.miniLabel}>Qty *</label>
                      <input type="number" step="0.01" min="0" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} style={styles.input} required />
                    </div>
                    <div style={{ flex: 1, minWidth: '90px' }}>
                      <label style={styles.miniLabel}>Unit *</label>
                      <input type="text" value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)} style={styles.input} required />
                    </div>
                  </div>

                  <div style={{ ...styles.itemRow, marginTop: '6px' }}>
                    <div style={{ flex: 1, minWidth: '110px' }}>
                      <label style={styles.miniLabel}>Rate *</label>
                      <input type="number" step="0.01" min="0" value={item.rate} onChange={(e) => handleItemChange(index, 'rate', e.target.value)} style={styles.input} placeholder="0" required />
                    </div>
                    <div style={{ flex: 1, minWidth: '90px' }}>
                      <label style={styles.miniLabel}>GST %</label>
                      <input type="number" step="0.01" min="0" value={item.gst_percent} onChange={(e) => handleItemChange(index, 'gst_percent', e.target.value)} style={styles.input} placeholder="optional" />
                    </div>
                    <div style={{ flex: 1, minWidth: '100px' }}>
                      <label style={styles.miniLabel}>Packaging</label>
                      <input type="text" value={item.packaging_qty} onChange={(e) => handleItemChange(index, 'packaging_qty', e.target.value)} style={styles.input} placeholder="e.g. 2 bags" />
                    </div>
                    <div style={{ flex: 2, minWidth: '160px' }}>
                      <label style={styles.miniLabel}>Item Remarks</label>
                      <input type="text" value={item.remarks} onChange={(e) => handleItemChange(index, 'remarks', e.target.value)} style={styles.input} placeholder="optional" />
                    </div>
                  </div>

                  <div style={styles.itemFooter}>
                    <span>Line Amount: <strong>{fmtMoney(lineAmount(item))}</strong></span>
                    {parseFloat(item.gst_percent) > 0 && (
                      <span>+ GST ({item.gst_percent}%): <strong>{fmtMoney(lineGst(item))}</strong></span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <button type="button" onClick={addItem} style={styles.addItemButton}>+ Add item</button>

            {/* Payment + totals */}
            <div style={styles.sectionLabel}>Payment</div>
            <div style={styles.row}>
              <div style={{ flex: 1, minWidth: '240px' }}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Amount Paid Now</label>
                  <input type="number" step="0.01" min="0" value={form.amount_paid} onChange={(e) => handleChange('amount_paid', e.target.value)} style={styles.input} placeholder="0" />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Payment Mode</label>
                  <select value={form.payment_mode} onChange={(e) => handleChange('payment_mode', e.target.value)} style={styles.input}>
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cheque">Cheque</option>
                    <option value="easypaisa">EasyPaisa</option>
                    <option value="jazzcash">JazzCash</option>
                  </select>
                </div>
              </div>

              <div style={styles.totalsBox}>
                <div style={styles.totalRow}><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
                {gstAmount > 0 && (
                  <div style={styles.totalRow}><span>GST</span><span>{fmtMoney(gstAmount)}</span></div>
                )}
                <div style={styles.totalRowGrand}><span>Grand Total</span><span>{fmtMoney(grandTotal)}</span></div>
                <div style={styles.totalRowBalance}><span>Balance</span><span>{fmtMoney(balance)}</span></div>
              </div>
            </div>

            <div style={styles.sectionLabel}>Remarks</div>
            <div style={styles.formGroup}>
              <textarea value={form.remarks} onChange={(e) => handleChange('remarks', e.target.value)} style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }} placeholder="Optional gate pass remarks..." rows="2" />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <span style={styles.footerHint}>💡 Saving creates invoice, deducts batch stock, and updates customer balance</span>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : '✓ Save Outward'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '1100px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '20px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: '160px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  miniLabel: { fontSize: '11px', fontWeight: '600', color: '#7f8c8d', marginBottom: '4px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  itemsList: { display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' },
  itemCard: { background: '#f8f9fa', borderRadius: '10px', padding: '14px', border: '1px solid #e0e0e0' },
  itemHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px dashed #e0e0e0' },
  itemIndex: { width: '24px', height: '24px', borderRadius: '50%', background: '#27ae60', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0 },
  itemTitle: { fontSize: '13px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.3px', flex: 1 },
  lineAmount: { fontSize: '14px', fontWeight: '700', color: '#2c3e50' },
  removeButton: { padding: '6px 12px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  itemRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  itemFooter: { marginTop: '10px', paddingTop: '8px', borderTop: '1px dashed #e0e0e0', display: 'flex', gap: '20px', fontSize: '12px', color: '#7f8c8d' },
  addItemButton: { width: '100%', padding: '12px', background: 'white', color: '#27ae60', border: '2px dashed #27ae60', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '700', marginBottom: '12px' },
  totalsBox: { flex: 1, minWidth: '260px', background: '#f8f9fa', borderRadius: '10px', padding: '14px 18px', height: 'fit-content' },
  totalRow: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#7f8c8d', padding: '4px 0' },
  totalRowGrand: { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '700', color: '#2c3e50', padding: '8px 0 4px', borderTop: '1px solid #e0e0e0', marginTop: '4px' },
  totalRowBalance: { display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '700', color: '#e74c3c', padding: '4px 0' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', alignItems: 'center', position: 'sticky', bottom: 0, background: 'white' },
  footerHint: { marginRight: 'auto', fontSize: '12px', color: '#7f8c8d' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default OutwardFormModal;